import { Config } from "@app/config";
import type { MeasureComponentProps, MeasureComponentState } from "@component/MeasureComponent";
import { MeasureComponent } from "@component/MeasureComponent";
import { Console } from "@shared/lib/console";
import { GoogleAnalytics4 } from "@shared/lib/google-analytics-4";
import type { GA4EventParameters } from "@shared/lib/google-analytics-4.type";
import type { ProgressButtonState } from "@shared/ui";
import {
	getLocale,
	getUrlParameter,
	getUuid,
	http,
	PromisePool,
	phoneCleanFormatter,
	SessionStorage,
} from "@shared/ui";
import autobind from "autobind-decorator";
import compact from "lodash/compact";
import type { ReactNode } from "react";
import { InView } from "react-intersection-observer";
import { LeadFormStepContainer } from "./Steps/LeadFormStepContainer";
import type {
	AbstractLeadCalculatorRequest,
	AbstractLeadCalculatorResponse,
	AbstractLeadFormFields,
	LeadRequest,
	LeadServiceResponse,
	ProductType,
} from "./types";

export interface StepsConfig {
	stepName: string;
	content: ReactNode;
}

export interface AbstractLeadFormProps<
	Request extends AbstractLeadCalculatorRequest = AbstractLeadCalculatorRequest,
	Response extends AbstractLeadCalculatorResponse = AbstractLeadCalculatorResponse,
	SearchParams extends Record<string, any> = Record<string, any>,
> extends MeasureComponentProps {
	title?: ReactNode;
	outsideTitle?: string;
	outsideTitleLink?: string;
	bottomLogo?: boolean;

	searchParams?: SearchParams;
	request?: Request;
	response?: Response;

	consultant?: string;
	consultantName?: string;
	consultantEmail?: string;
	source?: string;

	fluid?: boolean;

	colorScheme?: "dark" | "light";

	className?: string;

	onSent?: (response: LeadServiceResponse) => void;

	id?: string;

	hideOutsideTitle?: boolean;

	crossSellOption?: boolean;
}

export interface AbstractLeadFormState<FormFields extends AbstractLeadFormFields = AbstractLeadFormFields>
	extends MeasureComponentState {
	activeStep: string;
	submitState: ProgressButtonState;
	values: FormFields;
	previousSteps: string[];
	colorScheme: "dark" | "light";
	leadId: number;
	stepNumber: number;
}

export const CORPORATE_PRODUCT_TYPES: ProductType[] = [
	"SME_LOAN",
	"SME_ACCOUNT",
	"FACTORING",
	"FGS",
	"CORPORATE_HEALTH_INSURANCE",
];

export abstract class AbstractLeadForm<
	Request extends AbstractLeadCalculatorRequest = AbstractLeadCalculatorRequest,
	Response extends AbstractLeadCalculatorResponse = AbstractLeadCalculatorResponse,
	FormFields extends AbstractLeadFormFields = AbstractLeadFormFields,
	Props extends AbstractLeadFormProps<Request, Response> = AbstractLeadFormProps<Request, Response>,
	State extends AbstractLeadFormState<FormFields> = AbstractLeadFormState<FormFields>,
> extends MeasureComponent<Props, State> {
	protected readonly promisePool: PromisePool = new PromisePool();
	private readonly isEmbed = getUrlParameter("embedJs", "0") === "1";
	private readonly bmLeadGa4EventUuid = getUuid();
	protected firstCalculation = true;
	protected calculationId = undefined;
	protected buttonId = undefined;
	protected lastStepValues: Partial<FormFields> = {};

	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			activeStep: "lead-form",
			submitState: undefined,
			values: {
				wantsCrossSell: false,
				wantsNewsletter: false,
			},
			previousSteps: [],
			colorScheme: "dark",
			leadId: undefined,
			stepNumber: 0,
		} as unknown as State;
	}

	public componentDidMount(): void {
		super.componentDidMount();

		const { colorScheme } = this.props;

		this.setState({
			colorScheme: getUrlParameter("colorScheme", colorScheme) === "light" ? "light" : "dark",
		});

		// Ha iframe akkor a szülő kezeli az impression-t
		if (!Config.isSsr() && this.isEmbed) {
			this.promisePool
				.add(
					new Promise<void>((resolve) => {
						window.addEventListener(
							"message",
							(event: MessageEvent) => {
								if (event.data === "impression") {
									resolve();
								}
							},
							false,
						);
					}),
				)
				.then(() => {
					this.onVisibilityChange(true);
				});
		}
	}

	public componentWillUnmount(): void {
		this.promisePool.cancel();
	}

	protected getCommonEventParameters(): Partial<GA4EventParameters> {
		const { leadId } = this.state;

		return {
			calculationId: this.calculationId,
			buttonId: this.buttonId,
			...super.getCommonEventParameters(),
			leadId,
		};
	}

	public render(): ReactNode {
		const { bottomLogo, fluid, id, hideOutsideTitle } = this.props;
		const { activeStep, wasVisible, previousSteps, colorScheme, stepNumber } = this.state;
		const className = this.getClassName();

		const container = (
			<LeadFormStepContainer
				onStep={this.onSubmitStep}
				stepNumber={stepNumber}
				previousSteps={previousSteps}
				activeStep={activeStep}
				ga4Event={this.getCommonEventParameters()}
				bottomLogo={bottomLogo}
				colorScheme={colorScheme}
				id={id}
			>
				{this.renderSteps()}
			</LeadFormStepContainer>
		);

		return (
			<InView
				as="div"
				className={className}
				{...GoogleAnalytics4.getInViewProps()}
				skip={wasVisible || this.isEmbed}
				onChange={this.onVisibilityChange}
			>
				{fluid ? (
					container
				) : (
					<div className="small-calculator-container">
						{!hideOutsideTitle ? this.renderOutsideTitle() : null}
						{container}
					</div>
				)}
			</InView>
		);
	}

	private renderOutsideTitle(): ReactNode {
		const { outsideTitle = <>&nbsp;</>, outsideTitleLink } = this.props;

		if (outsideTitle) {
			if (outsideTitleLink && typeof outsideTitleLink === "string" && outsideTitleLink.length > 0) {
				return (
					<div className="small-calculator-title">
						<a href={outsideTitleLink} target="_blank" className="text-reset">
							{outsideTitle}
						</a>
					</div>
				);
			}

			return <div className="small-calculator-title">{outsideTitle}</div>;
		}
	}

	protected abstract renderSteps(): ReactNode;

	@autobind
	private onSubmitStep(target: string, lastStepValues: FormFields): void {
		const { values } = this.state;
		this.lastStepValues = lastStepValues;

		this.setState(
			{
				values: { ...values, ...lastStepValues },
			},
			() => {
				this.onStep(target, true);
			},
		);
	}

	protected onStep(target: string, withEvent: boolean): void {
		const { ga4Event } = this.props;
		const { activeStep: lastStep, previousSteps, stepNumber } = this.state;

		if (previousSteps[previousSteps.length - 1] === target) {
			previousSteps.pop();
		} else {
			previousSteps.push(lastStep);
		}

		this.setState({
			activeStep: target,
			stepNumber: stepNumber + 1,
			previousSteps,
		});

		if (withEvent) {
			this.buttonId = ga4Event?.buttonId || GoogleAnalytics4.generateId("bi");

			this.sendEvent("BM_step", {
				stepName: lastStep,
				stepNumber: stepNumber + 1,
				...this.lastStepValues,
			});
		}
	}

	private crossSellNewsletters(): void {
		const {
			values: { wantsCrossSell, wantsNewsletter, email, firstName, lastName },
		} = this.state;

		if (wantsNewsletter || wantsCrossSell) {
			const productTypes = this.productTypes.split(",");
			const newsLetterData = {
				subscriptionTypes: [
					CORPORATE_PRODUCT_TYPES.includes(this.getProductType())
						? "CORPORATE_NEWSLETTER"
						: "RETAIL_NEWSLETTER",
				],
				productTypes,
				firstName,
				lastName,
				email,
			};

			if (wantsCrossSell) {
				newsLetterData.subscriptionTypes = [...newsLetterData.subscriptionTypes, "CROSS_SELL"];
			}

			this.promisePool
				.add(http.post(Config.getWpRestApiUrl(`/subscribe/${encodeURIComponent(email)}`), newsLetterData))
				.catch((e) => {
					Console.error(e);
				});
		}
	}

	protected async sendLead(): Promise<LeadServiceResponse> {
		const { ga4Event, response: responseProps } = this.props;
		const { values } = this.state;
		this.setState({
			submitState: "loading",
		});

		const leadData = this.getLeadData();
		const templateName = this.getAutoresponderName();

		if (templateName) {
			leadData.autoresponder = {
				templateName,
				fields: this.getAutoresponderFields(),
			};
		}

		if (!leadData.request?.productRequisitionTime?.length) {
			leadData.request.productRequisitionTime = undefined;
		}

		this.crossSellNewsletters();

		return this.promisePool
			.add(http.post(Config.getWpRestApiUrl("/lead"), leadData))
			.then((response: LeadServiceResponse) => {
				if (!response?.leadId) {
					throw new Error("Lead ID is missing");
				}

				this.onSent(response);

				this.buttonId = ga4Event?.buttonId || GoogleAnalytics4.generateId("bi");

				this.sendEvent("BM_lead", {
					leadUuid: this.bmLeadGa4EventUuid,
					position: ga4Event?.position || 1,
					leadType: ga4Event?.leadType || this.getLeadType(),
					...values,
					user_data: {
						email_address: values.email,
						phone_number: values.phone,
						address: {
							last_name: values.lastName,
							first_name: values.firstName,
							city: values.cityName,
						},
					},
					providerName: responseProps?.bankName,
					productName: responseProps?.productName,
					productId: responseProps?.id,
				});

				this.setState({
					submitState: "done",
				});

				return response;
			})
			.catch((reason) => {
				this.setState({
					submitState: "error",
				});

				throw reason;
			})
			.finally(() => {
				this.promisePool.addTimeout().then(() => {
					this.setState({
						submitState: undefined,
					});
				});
			});
	}

	protected onSent(response: LeadServiceResponse): void {
		const { onSent } = this.props;
		const { leadId } = response;

		this.setState(
			{
				leadId,
			},
			() => {
				if (typeof onSent === "function") {
					onSent(response);
				}
			},
		);
	}

	protected sendCalculationEvent(koMessages: string[] = []): void {
		const { values } = this.state;
		const { ga4Event } = this.props;

		this.calculationId = ga4Event?.calculationId || GoogleAnalytics4.generateId("ci");

		this.sendEvent("BM_calculation", {
			hasDetailed: false,
			isDetailed: false,
			koMessages: koMessages?.join("|"),
		});

		if (this.firstCalculation) {
			this.sendEvent("BM_defaultParams", this.getInitialState().values);
			this.firstCalculation = false;
		}

		this.sendEvent("BM_calculationParams", {
			...values,
		});

		if (koMessages.length === 0) {
			this.sendEvent("BM_result");
		}
	}

	protected abstract getAutoresponderFields(): Record<string, any>;
	protected abstract getAutoresponderName(): string;

	protected getLeadData(): LeadRequest<Request, Response> {
		const { consultant = getUrlParameter("consultant") } = this.props;
		const {
			values: { firstName, lastName, phone, email, cityName, cityId, wantsCrossSell, wantsNewsletter },
		} = this.state;
		const calculatorLang = getLocale();

		let sourceBrowser = navigator?.userAgent;

		if ("ga" in window) {
			sourceBrowser = `GA + ${sourceBrowser}`;
		}

		if ("google_tag_manager" in window) {
			sourceBrowser = `GTM + ${sourceBrowser}`;
		}

		// Minden adat behúzása
		return {
			firstName,
			lastName,
			phone: phoneCleanFormatter(phone),
			email,
			cityName,
			cityId,
			wantsCrossSell,
			wantsNewsletter,

			calculatorType: this.getProductType(),
			calculatorLang,

			productType: this.getProductType(),

			consultant,
			source: this.getSource(),
			sourceBrowser,
			sourcePage: window.location.href,
			leadUuid: this.bmLeadGa4EventUuid,

			calculationData: this.getCalculationData(),
			request: { ...this.getLeadDataRequest() },
			response: {
				...this.getLeadDataResponse(),
				lastViewedContent: SessionStorage.get("bm-last-viewed-article"),
			},
		};
	}

	protected getCalculatorRequest(): Request {
		const { request = {} as Request } = this.props;

		return request;
	}

	protected getLeadDataRequest(): Request {
		return this.getCalculatorRequest();
	}

	protected getCalculatorResponse(): Response {
		const { response } = this.props;

		return response;
	}

	protected getLeadDataResponse(): Response {
		return this.getCalculatorResponse();
	}

	protected getCalculationData(): string {
		const { searchParams } = this.props;

		const searchParamsWithComparator = { comparator: undefined, ...searchParams };

		if (searchParamsWithComparator?.sort && !searchParamsWithComparator?.comparator) {
			searchParamsWithComparator.comparator = searchParamsWithComparator?.sort;
		}

		return JSON.stringify(searchParamsWithComparator);
	}

	protected getSource(): string {
		const { source = Config.getLeadSource() } = this.props;

		return source;
	}

	protected getClassName(): string {
		const { fluid, className, hideOutsideTitle, outsideTitle, bottomLogo } = this.props;
		const { colorScheme } = this.state;

		return compact([
			`small-calculator small-calculator-${colorScheme}`,
			fluid && "small-calculator-fluid",
			className,
			(hideOutsideTitle || !outsideTitle) && "hide-outside-title",
			!bottomLogo && "hide-bottom-logo",
		]).join(" ");
	}

	protected abstract getProductType(): ProductType;

	protected getLeadType(): ProductType {
		return this.getProductType();
	}
}
